import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import InfoIcon from '@mui/icons-material/Info';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ButtonLoading from '@mui/lab/LoadingButton';
import {
  useGetTenantConfigData,
  useGetTenantUsers,
  useSaveMyViewDashboard,
  useDeleteMyViewDashboard,
} from 'services/v1/Tenant/ConfigurationService';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  GridRowOrderChangeParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { Autocomplete, Button, Chip, IconButton, TextField, Tooltip } from '@mui/material';
import { ModalSelectMyViewItem } from './ModalSelectMyViewItem';
import {
  MyViewDashboardDefinitionItem,
  MyViewItemRequest,
  MyViewWorkFlowItem,
  PeopleMyViewItemConfig,
  RequestSaveMyViewAdditionalConfig,
  RequestSaveMyViewDashboard,
} from 'types/api/Tenant/ConfigurationTypes';
import { BackToListButton } from 'components/ButtonComponent/ButtonBackToList';
import { ModalMyViewAdditionalConfig } from './ModalMyViewAdditionalConfig';
import { NAVIGATION_COMPONENT_TYPE } from 'constant/NavigationConstant';
import { useFormik } from 'formik';
import { ChevronRightOutlined } from '@mui/icons-material';
import { userSelectionDataGridStyle } from 'pages/TenantMaster/MasterTable/TenantTable/AddTenantPage';
import { ViewDetailsMode } from '.';
import { useGetSolutionDefinitionDropdown } from 'services/v1/SystemTenant/AROKMS/SolutionDefinitionService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ModalConfirmationComponent } from 'components/ModalComponent/ModalConfirmationComponent';
import { toast } from 'react-toastify';
import { getMyViewSelectionId } from './MyViewSelectionUtils';
import { MyViewDashboardDescriptionEditor } from './MyViewDashboardDescriptionEditor';

function updateRowPosition(oldIndex: number, newIndex: number, rows: Array<GridRowModel<PeopleMyViewItemConfigState>>) {
  const rowsClone = [...rows];
  const [row] = rowsClone.splice(oldIndex, 1);
  rowsClone.splice(newIndex, 0, row);
  for (let i = 0; i < rowsClone.length; i++) {
    rowsClone[i].colIndexOrder = i + 1;
  }
  return rowsClone;
}

interface MyViewDashboardDetailComponentProps {
  onClose?: () => void;
  selectedDashboard: MyViewDashboardDefinitionItem | null;
  detailMode: ViewDetailsMode | null;
}

const initialModalMyViewConfigValue = {
  show: false,
  defaultXDimension: null,
  defaultYDimension: null,
  defaultDimensionFilter: null,
};

const sectionCardStyle = {
  border: '1px solid #EEF2F7',
  borderRadius: 1.5,
  p: 2,
};

interface FormTableAnalyticParameter {
  dashboardName: string | null;
  description: string;
  solutionGroup: AutoCompleteItem | null;
  userIds: string[];
  myViewItems: MyViewItemRequest[];
}

type PeopleMyViewItemConfigState = Omit<PeopleMyViewItemConfig, 'id'> & {
  id: string | null;
  ktreeDefinitionId: string | null;
  ktreeName?: string | null;
};

export const formikValidationSchema = Yup.object().shape({
  dashboardName: Yup.string().required('Dashboard Name is required').nullable(),
  solutionGroup: Yup.object().required('Solution is required').nullable(),
});

const formikInitialValues: FormTableAnalyticParameter = {
  dashboardName: null,
  description: '',
  solutionGroup: null,
  userIds: [],
  myViewItems: [],
};

export const MyViewDashboardDetail = (props: MyViewDashboardDetailComponentProps) => {
  const { onClose, selectedDashboard, detailMode } = props;

  const { isLoading } = useGetTenantConfigData({ refetchInterval: false });
  const [showModalComponentList, setShowModalComponentList] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMyViewConfig, setModalMyViewConfig] = useState(initialModalMyViewConfigValue);
  const [selectedUserViewConfig, setSelectedUserViewConfig] = useState<PeopleMyViewItemConfig | null>(null);
  const [rowData, setRowData] = useState<PeopleMyViewItemConfigState[]>([]);
  const { data: tenantUserList, isLoading: isLoadingUsers } = useGetTenantUsers();
  const { data: solutionGroupDropdown } = useGetSolutionDefinitionDropdown();
  const formik = useFormik<FormTableAnalyticParameter>({
    initialValues: formikInitialValues,
    validationSchema: formikValidationSchema,
    onSubmit: (values, formikHelper) => {
      formikHelper.resetForm();
    },
  });

  const { mutate: saveMyViewDashboard, isLoading: isSubmitting } = useSaveMyViewDashboard();
  const { mutate: deleteMyViewDashboard, isLoading: isDeleting } = useDeleteMyViewDashboard();

  const handleOnClose = () => {
    onClose?.();
    formik.resetForm();
  };

  const handleOnSubmitDefaultLoginView = (myViewId: string) => {
    setRowData((prevData) =>
      prevData.map((row) => ({
        ...row,
        defaultLoginView: row.id === myViewId,
      }))
    );
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'componentName',
        headerName: 'Page Name',
        flex: 1,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction='row' alignItems='center' gap={1}>
            <span>{params.row.componentName}</span>
            {params.row.defaultLoginView && (
              <Chip
                size='small'
                label='Login View'
                color='primary'
                variant='outlined'
                sx={{ fontSize: 10, height: 18, borderRadius: 1 }}
              />
            )}
          </Stack>
        ),
      },
      {
        field: 'componentMember',
        headerName: 'Member Filter',
        width: 170,
        renderCell: (params: GridRenderCellParams) => {
          if (!params.row.componentMember)
            return <Typography variant='caption' color='text.disabled'>—</Typography>;
          return <span>{params.row.componentMember}</span>;
        },
      },
      {
        field: 'defaultDimensionFilter',
        headerName: 'Dimension',
        width: 130,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.componentType !== NAVIGATION_COMPONENT_TYPE.INSIGHT_VIEW.toString())
            return <Typography variant='caption' color='text.disabled'>N/A</Typography>;
          return params.row.defaultYDimension ? (
            <Chip size='small' label='Configured' color='success' variant='outlined' sx={{ fontSize: 10 }} />
          ) : (
            <Chip size='small' label='Default' variant='outlined' sx={{ fontSize: 10, color: '#98A2AE' }} />
          );
        },
      },
      {
        field: 'additionalWorkflows',
        headerName: 'Drill Down',
        width: 130,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.componentType !== NAVIGATION_COMPONENT_TYPE.INSIGHT_VIEW.toString())
            return <Typography variant='caption' color='text.disabled'>N/A</Typography>;
          const additionalWorkFlows = params.row.additionalWorkFlows;
          const tooltipValues = additionalWorkFlows?.map((item: MyViewWorkFlowItem) => (
            <div key={item.componentName}>{item.componentName}</div>
          ));
          return (
            <Stack direction='row' alignItems='center' gap={0.5}>
              <Chip
                size='small'
                label={`${additionalWorkFlows?.length ?? 0} flow(s)`}
                variant='outlined'
                sx={{ fontSize: 10 }}
              />
              <Tooltip title={tooltipValues}>
                <IconButton size='small'>
                  <InfoIcon fontSize='inherit' color='primary' />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 90,
        getActions: (params: GridRowParams) => {
          const additionalConfigButton = (
            <Tooltip title='Page Settings'>
              <IconButton
                onClick={() => {
                  setModalMyViewConfig({
                    show: true,
                    defaultXDimension: params.row.defaultXDimension,
                    defaultYDimension: params.row.defaultYDimension,
                    defaultDimensionFilter: params.row.defaultDimensionFilter,
                  });
                  setSelectedUserViewConfig(params.row as PeopleMyViewItemConfig);
                }}
                size='small'
              >
                <SettingsOutlinedIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          );
          const defaultLoginViewButton = (
            <Tooltip title='Set as Default Login View'>
              <IconButton onClick={() => handleOnSubmitDefaultLoginView(params.row.id)} size='small'>
                <VpnKeyOutlinedIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          );
          if (params.row.defaultLoginView) return [additionalConfigButton];
          return [additionalConfigButton, defaultLoginViewButton];
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const userColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: 'Full Name',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.dashboard != null) {
            return (
              <Tooltip title={`Already assigned to "${params.row.dashboard?.label}"`}>
                <span style={{ color: '#98A2AE' }}>{params.value}</span>
              </Tooltip>
            );
          }
          return <span>{params.value}</span>;
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 180,
      },
    ],
    []
  );

  const selectedItemIds: Set<string> = useMemo(() => {
    const ids = rowData.map((item) => getMyViewSelectionId(item.componentId, item.componentType, item.subjectId));
    return new Set(ids);
  }, [rowData]);

  const handleOnAddMyViewComponent = (data: MyViewItemRequest[]) => {
    const newRecordInsertionId = `NEW-${new Date()}`;
    const updateRow: PeopleMyViewItemConfigState[] = data.map((item, index) => {
      const itemSelectionId = getMyViewSelectionId(item.id, item.componentType, item.subjectId);
      const existingItem = rowData.find(
        (existing) =>
          getMyViewSelectionId(existing.componentId, existing.componentType, existing.subjectId) === itemSelectionId
      );
      if (existingItem) {
        return { ...existingItem, colIndexOrder: item.colIndexOrder || existingItem.colIndexOrder };
      }
      return {
        id: item.id ? item.id : newRecordInsertionId,
        componentId: item.id,
        componentName: item.componentName,
        componentType: item.componentType,
        componentMember: null,
        componentMemberId: null,
        subjectId: item.subjectId ?? null,
        defaultLoginView: false,
        additionalWorkFlows: [],
        defaultXDimension: null,
        defaultYDimension: null,
        defaultDimensionFilter: null,
        ktreeDefinitionId: item.ktreeDefinitionId,
        ktreeName: item.ktreeName || undefined,
        cubeDefinitionId: item.cubeDefinitionId || undefined,
        colIndexOrder: item.colIndexOrder || index + 1,
        currentPeriodOffset: item.currentPeriodOffset || 0,
        defaultMeasurement: item.defaultMeasurement || null,
        tableAnalyticDefaultColumn: item.tableAnalyticDefaultColumn || null,
        tableAnalyticDefaultRow: item.tableAnalyticDefaultRow || null,
      };
    });
    setRowData(updateRow);
  };

  const handleOnAddMyViewConfig = (data: RequestSaveMyViewAdditionalConfig) => {
    setRowData((prevRowData) =>
      prevRowData.map((row) => {
        if (row.id !== data.myViewId) return row;
        return {
          ...row,
          defaultXDimension: data.defaultXDimension,
          defaultYDimension: data.defaultYDimension,
          additionalWorkFlows: [
            {
              componentId: data.additionalWorkFlows[0],
              componentType: '',
              componentName: '',
              indexOrder: 1,
              id: null,
            },
          ],
          defaultDimensionFilter: data.defaultFilterMember,
          componentMemberId: data.memberId,
          componentMember: data.memberName,
          currentPeriodOffset: data.currentPeriodOffset,
          defaultMeasurement: data.defaultMeasurement,
          tableAnalyticDefaultColumn: data.tableAnalyticDefaultColumn,
          tableAnalyticDefaultRow: data.tableAnalyticDefaultRow,
        };
      })
    );
  };

  const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
    const newRows = updateRowPosition(params.oldIndex, params.targetIndex, rowData);
    setRowData(newRows);
  };

  const handleOnSaveDashboard = () => {
    if (formik.values.dashboardName && formik.values.solutionGroup?.value) {
      const payload: RequestSaveMyViewDashboard = {
        id: selectedDashboard?.id ? selectedDashboard.id : null,
        dashboardName: formik.values.dashboardName,
        description: formik.values.description || null,
        solutionDefinitionId: formik.values.solutionGroup?.value.toString(),
        myViewItems: rowData.map((item) => ({
          id: item.componentId,
          componentName: item.componentName,
          componentType: item.componentType,
          subjectId: item.subjectId,
          defaultXDimension: item.defaultXDimension,
          defaultYDimension: item.defaultYDimension,
          defaultFilterMember: item.defaultDimensionFilter,
          memberId: item.componentMemberId,
          additionalWorkFlows: item.additionalWorkFlows.map((w) => w.componentId).filter(Boolean),
          additionalConfig: null,
          ktreeDefinitionId: item.ktreeDefinitionId,
          ktreeName: item.ktreeName || null,
          cubeDefinitionId: item.cubeDefinitionId || null,
          colIndexOrder: item.colIndexOrder,
          currentPeriodOffset: item.currentPeriodOffset,
          defaultMeasurement: item.defaultMeasurement,
          tableAnalyticDefaultColumn: item.tableAnalyticDefaultColumn,
          tableAnalyticDefaultRow: item.tableAnalyticDefaultRow,
        })),
        userIds: formik.values.userIds,
      };
      saveMyViewDashboard(payload, {
        onSuccess: () => {
          onClose?.();
          formik.resetForm();
        },
      });
    }
  };

  const handleApproveDeleteDashboard = () => {
    if (selectedDashboard?.id) {
      deleteMyViewDashboard(selectedDashboard.id, {
        onSuccess: () => {
          toast.success('Dashboard deleted successfully!');
          setShowDeleteModal(false);
          onClose?.();
          formik.resetForm();
        },
      });
    } else {
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    if (selectedDashboard) {
      formik.setValues({
        dashboardName: selectedDashboard.dashboardName,
        description: selectedDashboard.description ?? '',
        userIds: selectedDashboard.users.map((user) => user.id),
        // @ts-ignore
        myViewItems: selectedDashboard.myViews,
        solutionGroup: selectedDashboard.solutionDefinition,
      });
      setRowData(selectedDashboard.myViews);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDashboard]);

  const isLoadingData = isLoading;

  return (
    <Box sx={{ bgcolor: '#fff', px: 2, py: 2, width: '100%' }}>
      {/* Header */}
      <Stack direction='row' alignItems='center' gap={1} mb={1}>
        <BackToListButton title='Back' onClick={handleOnClose} />
        <Typography color='text.disabled' mx={0.5}>|</Typography>
        <Typography variant='h6' color='text.primary' fontWeight='bold'>
          {detailMode === ViewDetailsMode.INSERT ? 'Add Dashboard' : 'Edit Dashboard'}
        </Typography>
        {detailMode === ViewDetailsMode.EDIT && (
          <>
            <ChevronRightOutlined fontSize='small' sx={{ color: 'text.disabled' }} />
            <Typography variant='h6' color='text.secondary'>
              {selectedDashboard?.dashboardName}
            </Typography>
          </>
        )}
      </Stack>
      <Divider sx={{ mb: 2.5 }} />

      {isLoading ? (
        <Stack justifyContent='center' height={200}>
          <ActivityIndicator />
        </Stack>
      ) : (
        <Stack direction='column' gap={3}>
          {/* ── Form fields ── */}
          <Stack direction='row' gap={2}>
            <Stack flex={1}>
              <Typography variant='input-label' fontWeight={800} mb={0.75}>
                Dashboard Name
              </Typography>
              <TextField
                name='dashboardName'
                hiddenLabel
                value={formik.values.dashboardName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.dashboardName && formik.errors.dashboardName)}
                helperText={formik.touched.dashboardName && formik.errors.dashboardName}
                variant='outlined'
                size='small'
                fullWidth
                placeholder='e.g. Sales Overview Dashboard'
              />
            </Stack>
            <Stack flex={1}>
              <Typography variant='input-label' fontWeight={800} mb={0.75}>
                Component
              </Typography>
              <Autocomplete
                options={solutionGroupDropdown?.data || []}
                value={formik.values.solutionGroup}
                fullWidth
                onBlur={formik.handleBlur}
                onChange={(event, newValue) => formik.setFieldValue('solutionGroup', newValue)}
                clearIcon={null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size='small'
                    placeholder='Select Component...'
                    name='solutionGroup'
                    error={Boolean(formik.touched.solutionGroup && formik.errors.solutionGroup)}
                    helperText={formik.touched.solutionGroup && formik.errors.solutionGroup}
                  />
                )}
              />
            </Stack>
          </Stack>

          {/* ── Pages section ── */}
          <Box sx={sectionCardStyle}>
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mb={1.5}>
              <Stack>
                <Typography variant='input-label' fontWeight={800}>
                  Dashboard Pages
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Pages are dashboard screen displays. Drag rows to reorder.
                </Typography>
              </Stack>
              <Button
                variant='secondary-table-panel'
                startIcon={<AddCircleOutlineOutlinedIcon />}
                onClick={() => setShowModalComponentList(true)}
                disabled={isLoadingData}
                sx={{ flexShrink: 0, ml: 2 }}
              >
                Select Pages
              </Button>
            </Stack>
            <Box>
              <DataGridPro
                key='COMPONENTS'
                autoHeight
                loading={isLoadingData}
                density='compact'
                getRowId={(row) => (row.id ? row.id : row.componentId)}
                rowCount={rowData.length}
                rows={rowData}
                columns={columns}
                rowReordering
                onRowOrderChange={handleRowOrderChange}
                filterMode='client'
                checkboxSelection={false}
                hideFooter
                sx={{ border: '1px solid #EEF2F7', borderRadius: 1 }}
              />
            </Box>
          </Box>

          {/* ── Description section ── */}
          <Box sx={sectionCardStyle}>
            <Stack mb={1.5}>
              <Typography variant='input-label' fontWeight={800}>
                Description
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Shown on the Home page above this dashboard's steps. Leave it empty to use the default text.
              </Typography>
            </Stack>
            <MyViewDashboardDescriptionEditor
              key={selectedDashboard?.id ?? 'new-dashboard'}
              initialContent={selectedDashboard?.description ?? ''}
              onChange={(description) => formik.setFieldValue('description', description)}
            />
          </Box>

          {/* ── Users section ── */}
          <Box sx={sectionCardStyle}>
            <Stack mb={1.5}>
              <Typography variant='input-label' fontWeight={800}>
                Assign Users
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Assign this dashboard to specific users so that it appears on their first login.
              </Typography>
            </Stack>
            <Box sx={{ height: 280 }}>
              {!isLoadingUsers && (
                <DataGridPro
                  autoHeight={false}
                  key='MUI_USERS'
                  loading={isLoadingUsers}
                  sx={{ ...userSelectionDataGridStyle, border: '1px solid #EEF2F7', borderRadius: 1 }}
                  density='compact'
                  rowCount={tenantUserList?.data?.length ?? 0}
                  rows={tenantUserList?.data || []}
                  isRowSelectable={(params) => params.row.dashboard == null}
                  getRowClassName={(params) => (params.row.dashboard != null ? 'insight-view-cell-unsorted' : '')}
                  columns={userColumns}
                  checkboxSelection
                  hideFooter
                  selectionModel={formik.values.userIds}
                  onSelectionModelChange={(newSelection) => {
                    if (tenantUserList?.data) {
                      formik.setFieldValue('userIds', newSelection);
                    }
                  }}
                />
              )}
            </Box>
            {formik.values.userIds.length > 0 && (
              <Typography variant='caption' color='text.secondary' mt={1} display='block'>
                {formik.values.userIds.length} user{formik.values.userIds.length !== 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>

          {/* ── Actions ── */}
          <Stack direction='row' justifyContent='flex-end' gap={2} pb={1}>
            {detailMode === ViewDetailsMode.EDIT && (
              <Button
                variant='main-table-panel-border'
                color='error'
                onClick={() => setShowDeleteModal(true)}
                disabled={isLoadingData || isDeleting}
                sx={{ minWidth: 120 }}
              >
                Delete
              </Button>
            )}
            <ButtonLoading
              variant='main-table-panel'
              onClick={handleOnSaveDashboard}
              disabled={isLoadingData || !formikValidationSchema.isValidSync(formik.values) || rowData.length === 0}
              loading={isSubmitting}
              sx={{ minWidth: 120 }}
            >
              Save
            </ButtonLoading>
          </Stack>
        </Stack>
      )}

      <ModalSelectMyViewItem
        visible={showModalComponentList}
        onClose={() => setShowModalComponentList(false)}
        selectedDashboard={selectedDashboard}
        onSave={handleOnAddMyViewComponent}
        selectedItemIds={selectedItemIds}
        existingRowData={rowData}
      />

      <ModalMyViewAdditionalConfig
        visible={modalMyViewConfig.show}
        defaultXDimensionTypeId={modalMyViewConfig.defaultXDimension}
        defaultYDimensionTypeId={modalMyViewConfig.defaultYDimension}
        defaultFilterMemberId={modalMyViewConfig.defaultDimensionFilter}
        onClose={() => setModalMyViewConfig(initialModalMyViewConfigValue)}
        onSubmit={handleOnAddMyViewConfig}
        selectedView={selectedUserViewConfig}
      />

      <ModalConfirmationComponent
        visible={showDeleteModal}
        title='Delete Dashboard'
        message='Are you sure you want to delete this dashboard?'
        onClose={() => setShowDeleteModal(false)}
        onCancel={() => setShowDeleteModal(false)}
        onApprove={handleApproveDeleteDashboard}
        messageType='error'
        labelButtonApprove='Delete'
      />
    </Box>
  );
};
